import { LEGAL_VERSION } from '@lista/shared-types';
import { create } from 'zustand';

/**
 * Cookie & device-storage consent (ePrivacy/GDPR, PDPL). Lista has no tracking, so there are
 * two categories only:
 * - strictly necessary - the refresh cookie, the offline page cache, the signed-in user kept
 *   for offline start (`lista.user`) and this choice itself: always on;
 * - preferences - the keys below. Kept in localStorage only with consent; otherwise in
 *   sessionStorage, so they last for the current visit and are gone when the tab closes.
 */
const KEY = 'lista.consent';
export const PREFERENCE_KEYS = [
  'lista.locale',
  'lista.theme',
  'lista.itemView',
  'lista.itemOrder',
  'lista.lastList',
  'lista.targetLists',
] as const;

interface StoredConsent {
  preferences: boolean;
  version: string;
  at: string;
}

function read(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as Partial<StoredConsent>) : null;
    return v && typeof v.preferences === 'boolean' ? (v as StoredConsent) : null;
  } catch {
    return null;
  }
}

interface ConsentState {
  /** The user has answered (either button, or saved choices). */
  decided: boolean;
  preferences: boolean;
  /** The settings panel was opened again from "Cookie settings". */
  settingsOpen: boolean;
  choose(preferences: boolean): void;
  closeSettings(): void;
}

const initial = read();

export const useConsent = create<ConsentState>((set) => ({
  decided: initial !== null,
  preferences: initial?.preferences ?? false,
  settingsOpen: false,
  choose: (preferences) => {
    const value: StoredConsent = {
      preferences,
      version: LEGAL_VERSION,
      at: new Date().toISOString(),
    };
    try {
      localStorage.setItem(KEY, JSON.stringify(value));
    } catch {
      /* storage unavailable: the choice lasts for this page view */
    }
    movePreferences(preferences);
    set({ decided: true, preferences, settingsOpen: false });
  },
  closeSettings: () => set({ settingsOpen: false }),
}));

export const openConsentSettings = () => useConsent.setState({ settingsOpen: true });

/** Moves the preference keys to the store the new choice allows, keeping their values. */
function movePreferences(persist: boolean) {
  try {
    const [from, to] = persist ? [sessionStorage, localStorage] : [localStorage, sessionStorage];
    for (const key of PREFERENCE_KEYS) {
      const value = from.getItem(key);
      if (value !== null) to.setItem(key, value);
      from.removeItem(key);
    }
  } catch {
    /* storage unavailable */
  }
}

const target = () => (useConsent.getState().preferences ? localStorage : sessionStorage);

/**
 * Storage for preference keys: use this instead of localStorage for anything in
 * PREFERENCE_KEYS. Never throws (private mode, blocked storage).
 */
export const preferenceStorage = {
  getItem(key: (typeof PREFERENCE_KEYS)[number]): string | null {
    try {
      return target().getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: (typeof PREFERENCE_KEYS)[number], value: string) {
    try {
      target().setItem(key, value);
    } catch {
      /* storage unavailable */
    }
  },
};
